import { useEffect } from 'react';
import toast from 'react-hot-toast';
import useGetTripMerge from '../../../API/hooks/trips/useGetTripMerge';
import usePatchTripMerge from '../../../API/hooks/trips/usePatchTripMerge';
import S from './TripMergeControlsStyles';

type Props = {
  fleetId: string;
  filters: Record<string, string[]>;
  setFilters: (filters: Record<string, string[]>) => void;
};

function TripMergeControls({ fleetId, filters, setFilters }: Props): JSX.Element | null {
  const { data: tripMerge } = useGetTripMerge(fleetId);
  const { mutate: patchTripMerge, isLoading: isPatching } = usePatchTripMerge(
    undefined,
    () => toast.error('Failed to update trip merge settings'),
  );

  const isMergeEnabled = !!tripMerge?.clientTripMergeEnabled;
  const isMergedFilterChecked = filters.isMergedTrip?.[0] === 'true';

  useEffect(() => {
    if (!tripMerge?.isMergedTripAvailable || isMergeEnabled || !isMergedFilterChecked) {
      return;
    }
    setFilters({
      ...filters,
      isMergedTrip: [],
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMergeEnabled, isMergedFilterChecked, tripMerge?.isMergedTripAvailable]);

  if (!tripMerge?.isMergedTripAvailable) {
    return null;
  }

  const handleToggleMerge = (): void => {
    if (isPatching) {
      return;
    }
    const nextMergeEnabled = !isMergeEnabled;
    if (!nextMergeEnabled && isMergedFilterChecked) {
      setFilters({
        ...filters,
        isMergedTrip: [],
      });
    }
    patchTripMerge({
      fleetId,
      clientTripMergeEnabled: nextMergeEnabled,
    });
  };

  const handleFilterChange = (): void => {
    if (!isMergeEnabled) {
      return;
    }
    setFilters({
      ...filters,
      isMergedTrip: isMergedFilterChecked ? [] : ['true'],
    });
  };

  return (
    <S.Wrapper>
      <div
        className={`filter-checkbox${isMergeEnabled ? '' : ' disabled'}`}
        onClick={handleFilterChange}
        onKeyDown={(e) => {
          if (!isMergeEnabled) {
            return;
          }
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleFilterChange();
          }
        }}
        role="checkbox"
        tabIndex={isMergeEnabled ? 0 : -1}
        aria-checked={isMergedFilterChecked}
        aria-disabled={!isMergeEnabled}
        aria-label="Merged trips"
      >
        <input
          type="checkbox"
          checked={isMergedFilterChecked}
          disabled={!isMergeEnabled}
          readOnly
        />
        <span className="checkbox" />
        <span className="label">Merged trips</span>
      </div>
      <button
        type="button"
        className="merge-toggle"
        onClick={handleToggleMerge}
        disabled={isPatching}
        aria-pressed={isMergeEnabled}
        aria-label={isMergeEnabled ? 'Unmerge Trips' : 'Merge Trips'}
      >
        <span className={`switch ${isMergeEnabled ? 'on' : ''}`}>
          <span className="knob" />
        </span>
        <span className="label">{isMergeEnabled ? 'Unmerge Trips' : 'Merge Trips'}</span>
      </button>
    </S.Wrapper>
  );
}

export default TripMergeControls;
